import React, { useState, useEffect, useCallback } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, isSelectableContext } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { menuRegistry } from "@/services/menuRegistry";
import { permissionService } from "@/services/permissionService";
import { piletLoader } from "@/services/piletLoader";
import {
  ftueSettingsService,
  type FtueSettings,
} from "@/services/ftueSettingsService";
import { fetchLocations } from "@/pages/ManageLocations/manageLocationsService";
import { LocationSelector } from "@/components/LocationSelector/LocationSelector";
import { OnboardingFlow } from "@/components/OnboardingFlow";
import { Button } from "@resideo/blueprint-oneportal";
import { GlobalHeader } from "@/components/GlobalHeader";
import { Spinner } from "@resideo/blueprint-react";
import apiService from "@/services/apiService";
import { cameraIcon, hvacIcon } from "@/assets/icons";
import {
  ACCOUNT_LEVEL_ROUTES,
  BILLING_FLAG_MAP,
  BOTH_LEVEL_ROUTES,
  CAMERA_FLAG_MAP,
  LOCATION_LEVEL_ROUTES,
} from "@/utils/constants";
import {
  PRO_PORTAL_REDIRECT_FEATURE_FLAG,
  redirectToProPortal,
} from "@/config/proPortal";
import type {
  MenuItem,
  MenuLevel,
  NavItem,
} from "@/utils/types";
import styles from "./Layout.module.css";

export const Layout: React.FC = () => {
  const {
    user,
    activeRole,
    hasPermission,
    logout,
    selectedContext,
    selectContext,
    selectedLocation,
    selectLocation,
    locations,
    isRefreshingPermissions,
    consumerUsers,
    selectedCompanyIndex,
    selectCompany,
  } = useAuth();
  useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<string[]>(["Cameras"]);
  const [registeredModules, setRegisteredModules] = useState(
    menuRegistry.getAllMenus(),
  );
  const [piletMenuItems, setPiletMenuItems] = useState(() =>
    piletLoader.getMenuItems(),
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [customerName, setCustomerName] = useState<string>("");
  const welcomeOnboardingEnabled = permissionService.isFeatureEnabled("welcome-onboarding");
  const legacyLandingPath = "/camera";
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [ftueSettings, setFtueSettings] = useState<FtueSettings | null>(null);
  const [isFtueSettingsLoading, setIsFtueSettingsLoading] = useState(false);
  const [isFtueSettingsUnavailable, setIsFtueSettingsUnavailable] = useState(false);
  const [hasLocallyCompletedOnboarding, setHasLocallyCompletedOnboarding] = useState(false);

  const newLocationIds = !isFtueSettingsUnavailable && ftueSettings?.ftue
    ? locations
      .map((item) => item.id)
      .filter((id) => !ftueSettings.seenLocationIds.includes(id))
    : [];

  const markLocationGuidanceComplete = useCallback(
    async (locationId: string) => {
      if (!user?.id) return;

      const currentSettings = ftueSettings ?? await ftueSettingsService.get(user.id);
      const updatedSettings = await ftueSettingsService.update(user.id, {
        seenLocationIds: [...currentSettings.seenLocationIds, locationId],
        seenFirstNewLocation: true,
      });
      setFtueSettings(updatedSettings);
    },
    [ftueSettings, user?.id],
  );

  useEffect(() => {
    let cancelled = false;

    setFtueSettings(null);
    setIsFtueSettingsLoading(false);
    setIsFtueSettingsUnavailable(false);
    setHasLocallyCompletedOnboarding(false);

    if (!welcomeOnboardingEnabled || !user?.id || selectedContext === "PARTNER") {
      return;
    }

    setIsFtueSettingsLoading(true);
    void (async () => {
      try {
        const settings = await ftueSettingsService.get(user.id);
        if (!cancelled) {
          setFtueSettings(settings);
          setIsFtueSettingsUnavailable(false);
        }
      } catch {
        // FTUE eligibility couldn't be determined (API + offline cache both
        // failed) — fail open to the dashboard rather than blocking the user.
        if (!cancelled) {
          setIsFtueSettingsUnavailable(true);
          setFtueSettings({
            ftue: true,
            seenLocationIds: [],
            seenDeviceTypes: [],
            seenFirstNewLocation: false,
          });
        }
      } finally {
        if (!cancelled) {
          setIsFtueSettingsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedContext, user?.id, welcomeOnboardingEnabled]);

  useEffect(() => {
    if (!welcomeOnboardingEnabled) {
      if (location.pathname === "/onboarding") {
        navigate(legacyLandingPath, { replace: true });
      }
      setShowOnboarding(false);
      return;
    }

    if (!user?.id) {
      if (location.pathname === "/onboarding") {
        navigate("/dashboard", { replace: true });
      }
      setShowOnboarding(false);
      return;
    }

    if (selectedContext === "PARTNER") {
      if (location.pathname === "/onboarding") {
        navigate("/mybusiness/home", { replace: true });
      }
      setShowOnboarding(false);
      return;
    }

    if (isFtueSettingsLoading || !ftueSettings) {
      if (!(showOnboarding && location.pathname === "/onboarding")) {
        setShowOnboarding(false);
      }
      return;
    }

    const shouldShowOnboarding = !ftueSettings.ftue && !hasLocallyCompletedOnboarding;

    if (shouldShowOnboarding && location.pathname !== "/onboarding") {
      navigate("/onboarding", { replace: true });
    } else if (!shouldShowOnboarding && location.pathname === "/onboarding") {
      navigate("/dashboard", { replace: true });
    }

    setShowOnboarding(
      shouldShowOnboarding && location.pathname === "/onboarding",
    );
  }, [
    ftueSettings,
    hasLocallyCompletedOnboarding,
    isFtueSettingsLoading,
    legacyLandingPath,
    location.pathname,
    navigate,
    selectedContext,
    showOnboarding,
    user?.id,
    welcomeOnboardingEnabled,
  ]);

  const dismissOnboarding = async () => {
    try {
      if (user?.id) {
        const currentSettings = ftueSettings ?? await ftueSettingsService.get(user.id);
        const currentLocations = await fetchLocations({ includeHealth: false });
        const updatedSettings = await ftueSettingsService.update(user.id, {
          ftue: true,
          seenLocationIds: [
            ...currentSettings.seenLocationIds,
            ...currentLocations.map((item) => item.id),
          ],
          seenDeviceTypes: [
            ...currentSettings.seenDeviceTypes,
            ...currentLocations.flatMap((item) => item.deviceTypeLabels),
          ],
        });
        setFtueSettings(updatedSettings);
      }
    } catch {
      // FTUE completion should not trap the user when temporary persistence fails.
      setHasLocallyCompletedOnboarding(true);
    } finally {
      if (welcomeOnboardingEnabled) {
        navigate("/dashboard", { replace: true });
      }
      setShowOnboarding(false);
    }
  };

  // Prefer firstName + lastName from the cached consumer-accounts response;
  // fall back to Auth0's user.name when consumer data isn't available yet
  // (e.g. PARTNER context, or before /v2/accounts has returned).
  useEffect(() => {
    const computeName = () => {
      try {
        const raw = localStorage.getItem("oneportal_consumer_data");
        if (raw) {
          const parsed = JSON.parse(raw);
          const firstName: string | undefined = parsed?.data?.firstName;
          const lastName: string | undefined = parsed?.data?.lastName;
          const combined = [firstName, lastName].filter(Boolean).join(" ").trim();
          if (combined) {
            setCustomerName(combined);
            return;
          }
        }
      } catch {
        /* ignore parse/storage errors */
      }
      setCustomerName(user?.name || "");
    };

    computeName();
    const handler = () => computeName();
    window.addEventListener("oneportal:consumer-data-refreshed", handler);
    return () =>
      window.removeEventListener("oneportal:consumer-data-refreshed", handler);
  }, [user, selectedContext, selectedCompanyIndex]);

  // Force re-render when context/role changes
  useEffect(() => {
    // Trigger re-render on context change
    setRegisteredModules(menuRegistry.getAllMenus());
  }, [selectedContext, activeRole]);

  // Listen for module menu registrations
  useEffect(() => {
    const interval = setInterval(() => {
      const modules = menuRegistry.getAllMenus();
      const loadedPiletMenus = piletLoader.getMenuItems();
      if (modules.length !== registeredModules.length) {
        setRegisteredModules(modules);
      }
      if (loadedPiletMenus.length !== piletMenuItems.length) {
        setPiletMenuItems(loadedPiletMenus);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [registeredModules, piletMenuItems.length]);

  // ─── Auto-redirect when switching between account/location level ───
  useEffect(() => {
    if (!isConsumerContext(selectedContext)) return;

    const path = location.pathname;
    const atAccountLevel = selectedLocation === null;

    if (atAccountLevel) {
      // Switched to account level — redirect away from location-only routes,
      // but allow routes that are valid at both levels (e.g. /camera, /camera/events)
      // and per-camera device routes that are accessible from the org-level camera grid
      // (currently settings, liveView, events, activity, and recording-schedule). All other per-camera routes remain
      // location-only as they require a location context.
      const isCameraDeviceRouteAtAccountLevel =
        /^\/camera\/[^/]+\/(settings|liveView|events|activity|recording-schedule)(\/|$)/.test(path);
      const isBothLevelRoute =
        BOTH_LEVEL_ROUTES.includes(path) || isCameraDeviceRouteAtAccountLevel;
      const onLocationRoute =
        !isBothLevelRoute &&
        LOCATION_LEVEL_ROUTES.some(
          (prefix) => path === prefix || path.startsWith(`${prefix}/`),
        );
      if (onLocationRoute) {
        navigate("/employees", { replace: true });
      }
    } else {
      // A specific location is selected — if the current route is account-only,
      // clear the selection so the menu/LocationSelector switch to My Organization.
      const onAccountOnlyRoute =
        path !== "/dashboard/location" &&
        ACCOUNT_LEVEL_ROUTES.some(
          (prefix) => path === prefix || path.startsWith(`${prefix}/`),
        );
      if (onAccountOnlyRoute) {
        selectLocation(null);
      }
    }
  }, [selectedLocation, selectedContext, location.pathname]);

  // Sync selectedLocation from URL params when landing on the location dashboard.
  useEffect(() => {
    if (location.pathname !== "/dashboard/location") return;
    const params = new URLSearchParams(location.search);
    const locationId = params.get("locationId");
    if (locationId && locationId !== selectedLocation?.id) {
      const match = locations.find((l) => l.id === locationId);
      if (match) selectLocation(match);
    }
  }, [location.pathname, location.search]);

  const handleLogout = () => {
    logout();
  };

  const handleManageResideoId = () => {
    const resideoIdUrl = import.meta.env.VITE_RESIDEO_ID_URL;
    if (resideoIdUrl) {
      window.open(resideoIdUrl, "_blank", "noopener,noreferrer");
    }
  };

  const toggleSubmenu = (label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const handleServiceProfessionalClick = async () => {
    await redirectToProPortal(async () => {
      await apiService.setMyAppSetting("preferredAccountType", "PARTNER");
    });
  };

  const contextLabels: Record<string, string> = {
    COMMERCIAL: t("layout.smallBusiness"),
    PARTNER: t("layout.serviceProfessional"),
  };
  const contextOptions =
    user?.contexts
      ?.filter((ctx) => isSelectableContext(ctx.context))
      .map((ctx) => ({
        value: ctx.context,
        label: contextLabels[ctx.context] || ctx.contextDisplayName || ctx.context,
      })) || [];

  const handleContextChange = (value: string) => {
    if (
      value === "PARTNER" &&
      permissionService.isFeatureEnabled(PRO_PORTAL_REDIRECT_FEATURE_FLAG)
    ) {
      void handleServiceProfessionalClick();
      return;
    }
    selectContext(value);
  };

  /**
   * Check if menu item should be visible
   * Shows menu if:
   * - Permission is null/empty/undefined (for development)
   * - Permission is "dev.placeholder" (explicit placeholder for developers)
   * - User has the required permission
   */
  const shouldShowMenuItem = (permission?: string): boolean => {
    if (!permission || permission === "" || permission === "dev.placeholder") {
      return true;
    }
    if (isRefreshingPermissions) return false;
    return hasPermission(permission);
  };

  // Navigation structure matching Figma design (updated 2025)
  const navItems: NavItem[] = [];
  const dashboardPath = selectedLocation
    ? `/dashboard/location?${new URLSearchParams({
      locationId: selectedLocation.id,
      locationName: selectedLocation.name,
      ...(selectedLocation.consumerAccountId
        ? { consumerAccountId: selectedLocation.consumerAccountId }
        : {}),
    }).toString()}`
    : "/dashboard";

  // Add menu items from piletLoader with context-based filtering
  const currentContext = selectedContext;

  // Filter pilet menu items based on requiredContext
  // RESIDENTIAL and COMMERCIAL are consumer-type contexts that match requiredContext: "CONSUMER"
  const isConsumerContext = (ctx: string | null): boolean => {
    return ctx === "RESIDENTIAL" || ctx === "COMMERCIAL";
  };
  const contextFilteredPiletMenus = piletMenuItems.filter((menu) => {
    // If menu has requiredContext, only show if it matches current context
    if (menu.requiredContext) {
      if (menu.requiredContext === "CONSUMER") {
        // Consumer menus should show for RESIDENTIAL and COMMERCIAL contexts
        return isConsumerContext(currentContext);
      }
      const shouldShow = menu.requiredContext === currentContext;
      return shouldShow;
    }
    // If no requiredContext, show for all contexts (like billing)
    return true;
  });

  // Add PRO-PORTAL section if we have PARTNER context menus
  const partnerMenus = contextFilteredPiletMenus.filter(
    (m) => m.requiredContext === "PARTNER",
  );
  if (partnerMenus.length > 0) {
    // Add Partner menus with proper structure (no header)
    partnerMenus.forEach((menu, index) => {
      if (menu.submenu && menu.submenu.length > 0) {
        // Menu with submenu
        navItems.push({
          label: menu.label,
          icon: menu.icon,
          menuLevel: "account",
          permission: menu.permission || "",
          submenu: menu.submenu.map((sub) => ({
            label: sub.label,
            path: sub.target || sub.path || "",
            permission: sub.permission || "",
          })),
        });
      } else if (menu.path) {
        // Flat menu item
        navItems.push({
          label: menu.label,
          path: menu.path,
          icon: menu.icon,
          menuLevel: "account",
          permission: menu.permission || "",
        });
      }

      // Add billing menu right after first partner menu (Home)
      if (index === 0 && permissionService.isFeatureEnabled("billing")) {
        const billingMenus = contextFilteredPiletMenus.filter(
          (m) => !m.requiredContext,
        );
        billingMenus.forEach((billingMenu) => {
          if (billingMenu.submenu && billingMenu.submenu.length > 0) {
            const filteredSubmenu = billingMenu.submenu
              .filter((sub: any) => {
                const subPath = sub.target || sub.path || "";
                const flag = BILLING_FLAG_MAP[subPath];
                return !flag || permissionService.isFeatureEnabled(flag);
              })
              .map((sub: any) => ({
                label: sub.label,
                path: sub.target || sub.path || "",
                permission: sub.permission || "",
              }));
            if (filteredSubmenu.length > 0) {
              navItems.push({
                label: billingMenu.label,
                icon: billingMenu.icon,
                menuLevel: "account",
                permission: billingMenu.permission || "",
                submenu: filteredSubmenu,
              });
            }
          } else if (billingMenu.target || billingMenu.path) {
            navItems.push({
              label: billingMenu.label,
              path: billingMenu.target || billingMenu.path || "",
              icon: billingMenu.icon,
              menuLevel: "account",
              permission: billingMenu.permission || "",
            });
          }
        });
      }
    });
  }

  // Add standard OnePortal menus if NOT in PARTNER context
  if (currentContext !== "PARTNER") {
    // Get CONSUMER pilet menus for placement across sections
    const consumerMenus = contextFilteredPiletMenus.filter(
      (m) => m.requiredContext === "CONSUMER",
    );
    const nonNotificationMenus = consumerMenus.filter(
      (menu) => !menu.label.toLowerCase().includes("notification"),
    );
    const notificationMenus = consumerMenus.filter((menu) =>
      menu.label.toLowerCase().includes("notification"),
    );

    // ─── TOP-LEVEL ITEMS ───
    if (permissionService.isFeatureEnabled("dashboard")) {
      // Org-level dashboard; once a location is selected the label becomes its name
      navItems.push({
        label: selectedLocation?.name || t("layout.dashboard"),
        path: dashboardPath,
        permission: "",
        menuLevel: "both",
      });

    }

    navItems.push(
      ...(permissionService.isFeatureEnabled("business-intelligence")
        ? [
          {
            label: t("layout.businessIntelligence"),
            path: "/business-intelligence",
            permission: "",
            menuLevel: "account" as MenuLevel,
          },
        ]
        : []),
      ...(permissionService.isFeatureEnabled("locations")
        ? [
          {
            label: t("layout.locations"),
            path: "/locations",
            permission: "",
            menuLevel: "both" as MenuLevel,
            icon: (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 21C12 21 19 15.25 19 10C19 6.13401 15.866 3 12 3C8.13401 3 5 6.13401 5 10C5 15.25 12 21 12 21Z"
                  stroke="currentColor"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 12.5C13.3807 12.5 14.5 11.3807 14.5 10C14.5 8.61929 13.3807 7.5 12 7.5C10.6193 7.5 9.5 8.61929 9.5 10C9.5 11.3807 10.6193 12.5 12 12.5Z"
                  stroke="currentColor"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ),
          },
        ]
        : []),
    );

    // ─── DEVICES SECTION ───
    navItems.push({
      type: "header",
      label: t("layout.devices"),
      menuLevel: "account",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M18.755 2C19.453 2 20.033 2.641 20.033 3.421V9.634C20.033 10.414 19.463 11.055 18.755 11.055H5.668C4.97 11.055 4.389 10.414 4.389 9.634V3.421C4.389 2.641 4.96 2 5.668 2H18.755ZM18.755 0H5.668C3.878 0 2.423 1.541 2.423 3.421V9.634C2.423 11.525 3.878 13.055 5.668 13.055H18.755C20.545 13.055 22 11.515 22 9.634V3.421C22 1.531 20.545 0 18.755 0Z"
            fill="currentColor"
          />
          <path
            d="M6.326 17.197C7.634 17.197 8.686 18.278 8.686 19.598C8.686 20.919 7.624 21.999 6.326 21.999C5.029 21.999 3.967 20.919 3.967 19.598C3.967 18.278 5.029 17.197 6.326 17.197ZM6.326 15.196C3.937 15.196 2 17.167 2 19.598C2 22.029 3.937 24 6.326 24C8.716 24 10.653 22.029 10.653 19.598C10.653 17.167 8.716 15.196 6.326 15.196Z"
            fill="currentColor"
          />
          <path
            d="M19.758 23.91H15.648C14.448 23.91 13.475 22.92 13.475 21.699V17.517C13.475 16.297 14.448 15.306 15.648 15.306H19.758C20.958 15.306 21.931 16.297 21.931 17.517V21.699C21.931 22.92 20.958 23.91 19.758 23.91ZM15.648 17.297C15.53 17.297 15.442 17.387 15.442 17.507V21.689C15.442 21.809 15.53 21.899 15.648 21.899H19.758C19.876 21.899 19.965 21.809 19.965 21.689V17.507C19.965 17.387 19.876 17.297 19.758 17.297H15.648Z"
            fill="currentColor"
          />
          <path
            d="M16.907 7.943V5.152C16.907 4.671 16.524 4.282 16.051 4.282H8.382C7.909 4.282 7.526 4.671 7.526 5.152V7.943C7.526 8.424 7.909 8.814 8.382 8.814H16.051C16.524 8.814 16.907 8.424 16.907 7.943Z"
            fill="currentColor"
          />
        </svg>
      ),
    });

    // Cameras (from pilet) — under DEVICES
    nonNotificationMenus.forEach((menu) => {
      const menuPath = menu.target || menu.path || "";
      const isCameraMenu = menuPath === "/camera" || menu.label === "Cameras";

      if (isCameraMenu && !permissionService.isFeatureEnabled("cameras")) {
        return;
      }

      const menuItem: any = {
        label: menu.label,
        permission: menu.permission || "",
        menuLevel: isCameraMenu ? "both" : "location",
        onExpand: menu.onExpand,
      };

      if (isCameraMenu && selectedLocation) {
        menuItem.icon = <img src={cameraIcon} alt="" aria-hidden="true" />;
      }

      if (menu.submenu && menu.submenu.length > 0) {
        const filteredSubmenu = menu.submenu
          .filter((sub: any) => {
            if (!isCameraMenu) return true;
            const subPath = sub.target || sub.path || "";
            const flag = CAMERA_FLAG_MAP[subPath];
            return !flag || permissionService.isFeatureEnabled(flag);
          })
          .map((sub: any) => {
            const subPath = sub.target || sub.path || "";
            // Live Cameras (/camera) and EventHistory (/camera/events) are visible at both levels;
            // other camera submenu items are location-only.
            const subMenuLevel: MenuLevel =
              isCameraMenu && (subPath === "/camera" || subPath === "/camera/events") ? "both" : "location";
            return {
              label: sub.label,
              path: subPath,
              permission: sub.permission || "",
              menuLevel: subMenuLevel,
              onHover: sub.onHover,
            };
          });
        if (filteredSubmenu.length === 0) return;
        menuItem.submenu = filteredSubmenu;
      } else if (menu.target || menu.path) {
        menuItem.path = menuPath;
      }

      navItems.push(menuItem);
    });

    // HVAC — under DEVICES (gated by feature flag; sub-items gated individually)
    const hvacSubmenu = [
      { label: t("layout.systemStatus"), path: "/hvac/status", flag: "hvac.status" },
      { label: t("layout.deviceHealth"), path: "/hvac/health", flag: "hvac.health" },
      { label: t("layout.schedule"), path: "/hvac/schedule", flag: "hvac.schedule" },
      { label: t("layout.reports"), path: "/hvac/reports", flag: "hvac.reports" },
      { label: t("layout.settings"), path: "/hvac/settings", flag: "hvac.settings" },
    ]
      .filter((item) => permissionService.isFeatureEnabled(item.flag))
      .map((item) => ({ label: item.label, path: item.path, permission: "" }));

    if (permissionService.isFeatureEnabled("hvac") && hvacSubmenu.length > 0) {
      navItems.push({
        label: t("layout.hvac"),
        path: "/hvac",
        permission: "",
        menuLevel: "location",
        icon: <img src={hvacIcon} alt="" aria-hidden="true" />,
        submenu: hvacSubmenu,
      });
    }

    // Security (formerly Access) — under DEVICES (gated by feature flags)
    const securitySubmenu = [
      { label: t("layout.systemStatus"), path: "/access/status", flag: "access.status" },
      { label: t("layout.deviceHealth"), path: "/access/health", flag: "access.health" },
      { label: t("layout.schedule"), path: "/access/schedule", flag: "access.schedule" },
      { label: t("layout.reports"), path: "/access/reports", flag: "access.reports" },
      { label: t("layout.settings"), path: "/access/settings", flag: "access.settings" },
    ]
      .filter((item) => permissionService.isFeatureEnabled(item.flag))
      .map((item) => ({ label: item.label, path: item.path, permission: "" }));

    if (
      permissionService.isFeatureEnabled("access") &&
      securitySubmenu.length > 0
    ) {
      navItems.push({
        label: t("layout.security"),
        path: "/access",
        permission: "",
        menuLevel: "location",
        submenu: securitySubmenu,
      });
    }

    // ─── OPERATIONS SECTION ───
    navItems.push({
      type: "header",
      label: t("layout.operations"),
      menuLevel: "account",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M16 3.88H18C18.53 3.88 19.04 4.09 19.41 4.47C19.79 4.85 20 5.35 20 5.88V19.88C20 20.41 19.79 20.92 19.41 21.29C19.03 21.67 18.53 21.88 18 21.88H6C5.47 21.88 4.96 21.67 4.59 21.29C4.21 20.91 4 20.41 4 19.88V5.88C4 5.35 4.21 4.84 4.59 4.47C4.97 4.09 5.47 3.88 6 3.88H8M12 10.95H16M8 10.95H8.01M12 15.63H16M8 15.63H8.01M9.34 2H14.67C15.15 2 15.54 2.39 15.54 2.87V4.89C15.54 5.37 15.15 5.76 14.67 5.76H9.34C8.86 5.76 8.47 5.37 8.47 4.89V2.87C8.47 2.39 8.86 2 9.34 2Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="square"
          />
        </svg>
      ),
    });

    // Alerts — under OPERATIONS
    if (permissionService.isFeatureEnabled("alerts")) {
      navItems.push({
        label: t("layout.alerts"),
        path: "/alerts",
        permission: "",
        menuLevel: "account",
      });
    }

    // Manage Devices — under OPERATIONS (visible for all at account level,
    // gated only on VITE_FF_DEVICES — mirrors the Users entry pattern).
    if (permissionService.isFeatureEnabled("devices")) {
      navItems.push({
        label: t("layout.manageDevices"),
        path: "/devices",
        permission: "",
        menuLevel: "account",
      });
    }

    // Users — under OPERATIONS (hidden for GUEST — client-side override since
    // the backend role-permissions API currently (incorrectly) grants user.view to GUEST)
    if (permissionService.isFeatureEnabled("employees") && activeRole !== "GUEST") {
      navItems.push({
        label: t("layout.users"),
        path: "/employees",
        permission: "user.view",
        menuLevel: "account",
      });
    }

    // Reports & Analytics — under OPERATIONS
    if (permissionService.isFeatureEnabled("reports")) {
      navItems.push({
        label: t("layout.reportsAnalytics"),
        path: "/reports",
        permission: "",
        menuLevel: "account",
      });
    }

    // Automations — under OPERATIONS
    if (permissionService.isFeatureEnabled("automations")) {
      navItems.push({
        label: t("layout.automations"),
        path: "/automations",
        permission: "",
        menuLevel: "account",
      });
    }

    // ─── SETTINGS SECTION ───
    navItems.push({
      type: "header",
      label: t("layout.settingsSection"),
      menuLevel: "account",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M19.766 12C19.766 11.527 19.716 11.064 19.634 10.609L21.189 9.223L19.022 5.459L17.045 6.118C16.342 5.518 15.531 5.05 14.647 4.732L14.225 2.687H9.886L9.464 4.732C8.58 5.05 7.768 5.518 7.066 6.118L5.089 5.459L2.921 9.223L4.477 10.609C4.395 11.059 4.345 11.523 4.345 12C4.345 12.477 4.395 12.937 4.477 13.391L2.921 14.777L5.089 18.541L7.066 17.882C7.768 18.482 8.58 18.95 9.464 19.268L9.886 21.314H14.225L14.647 19.268C15.531 18.95 16.342 18.482 17.045 17.882L19.022 18.541L21.189 14.777L19.634 13.391C19.716 12.941 19.766 12.477 19.766 12Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeMiterlimit="10"
          />
          <circle
            cx="12"
            cy="12"
            r="4"
            stroke="currentColor"
            strokeWidth="2"
            strokeMiterlimit="10"
          />
        </svg>
      ),
    });

    // Notifications (from pilet) — under SETTINGS
    if (permissionService.isFeatureEnabled("notifications")) {
      notificationMenus.forEach((menu) => {
        const menuItem: any = {
          label: menu.label,
          permission: menu.permission || "",
          menuLevel: "account",
        };

        if (menu.submenu && menu.submenu.length > 0) {
          menuItem.submenu = menu.submenu.map((sub) => ({
            label: sub.label,
            path: sub.target || sub.path || "",
            permission: sub.permission || "",
          }));
        } else if (menu.target || menu.path) {
          menuItem.path = menu.target || menu.path || "";
        }

        navItems.push(menuItem);
      });
    }

    // Billing & Plans (from pilet) — under SETTINGS
    if (permissionService.isFeatureEnabled("billing")) {
      const billingMenus = contextFilteredPiletMenus.filter(
        (m) => !m.requiredContext,
      );
      billingMenus.forEach((billingMenu) => {
        if (billingMenu.submenu && billingMenu.submenu.length > 0) {
          const filteredSubmenu = billingMenu.submenu
            .filter((sub: any) => {
              const subPath = sub.target || sub.path || "";
              const flag = BILLING_FLAG_MAP[subPath];
              return !flag || permissionService.isFeatureEnabled(flag);
            })
            .map((sub: any) => ({
              label: sub.label,
              path: sub.target || sub.path || "",
              permission: sub.permission || "",
            }));
          if (filteredSubmenu.length > 0) {
            navItems.push({
              label: billingMenu.label,
              permission: billingMenu.permission || "",
              menuLevel: "account",
              submenu: filteredSubmenu,
            });
          }
        } else if (billingMenu.target || billingMenu.path) {
          navItems.push({
            label: billingMenu.label,
            path: billingMenu.target || billingMenu.path || "",
            permission: billingMenu.permission || "",
            menuLevel: "account",
          });
        }
      });
    }

    // Company Settings — under SETTINGS
    if (permissionService.isFeatureEnabled("org-settings")) {
      navItems.push({
        label: t("layout.companySettings"),
        path: "/settings/company",
        permission: "",
        menuLevel: "account",
      });
    }
  } // End of: else (non-PARTNER context)

  // Add pilet menu items (Pro-Portal menus will show only for PARTNER context)
  registeredModules.forEach((module) => {
    navItems.push({
      label: module.label,
      icon: module.icon,
      permission: module.permission,
      submenu: module.submenu,
    });
  });

  // ─── MENU LEVEL FILTER (account vs location) ───
  // For CONSUMER contexts, filter nav items based on whether the user is at
  // account level (My Organization / selectedLocation === null) or location level.
  // PARTNER context is unaffected — all items are shown.
  const isConsumerCtx = isConsumerContext(currentContext);
  const isAccountLevel = selectedLocation === null;
  const activeMenuLevel: MenuLevel = isAccountLevel ? "account" : "location";

  const levelFilteredItems = isConsumerCtx
    ? navItems.filter((item) => {
      const level = "menuLevel" in item ? item.menuLevel : undefined;
      if (!level) {
        // Items without menuLevel pass through (backward compat for legacy registeredModules)
        if (import.meta.env.DEV) {
          const label = "label" in item ? item.label : "unknown";
          console.warn(
            `[Layout] Menu item "${label}" is missing menuLevel. Please add menuLevel: 'account' | 'location' | 'both'.`,
          );
        }
        return true;
      }
      return level === activeMenuLevel || level === "both";
    })
    : navItems;

  // ─── SUBMENU LEVEL FILTER ───
  // For CONSUMER contexts, filter each item's submenu by menuLevel so that
  // individual submenu items can declare their own visibility per level.
  // Submenu items without menuLevel pass through (backward compat).
  const submenuFilteredItems = isConsumerCtx
    ? levelFilteredItems
      .map((item) => {
        if (!("submenu" in item) || !item.submenu || item.submenu.length === 0) {
          return item;
        }
        const filtered = item.submenu.filter((sub) => {
          if (!sub.menuLevel) return true;
          return sub.menuLevel === activeMenuLevel || sub.menuLevel === "both";
        });
        if (filtered.length === 0) return null;
        return { ...item, submenu: filtered };
      })
      .filter((item): item is NavItem => item !== null)
    : levelFilteredItems;

  // First pass: filter items by permission
  const filteredItems = submenuFilteredItems.filter((item) => {
    // Skip headers in first pass - we'll handle them in second pass
    if ("type" in item && item.type === "header") return true;

    // For items with submenus, only show if at least one submenu item is visible
    if ("submenu" in item && item.submenu && item.submenu.length > 0) {
      const hasVisibleSubmenu = item.submenu.some((subItem) =>
        shouldShowMenuItem(subItem.permission),
      );
      return hasVisibleSubmenu;
    }

    // For regular items, check their permission
    if (!("permission" in item)) return true;
    return shouldShowMenuItem(item.permission);
  });

  // Second pass: remove headers that have no items following them
  const visibleNavItems = filteredItems.filter((item, index) => {
    // If it's a header, check if there are any non-header items after it
    if ("type" in item && item.type === "header") {
      if (item.label === t("layout.devices") && isAccountLevel) {
        return true;
      }

      // Look for the next header or end of array
      let hasItemsInSection = false;
      for (let i = index + 1; i < filteredItems.length; i++) {
        const nextItem = filteredItems[i];
        // If we hit another header, stop looking
        if ("type" in nextItem && nextItem.type === "header") break;
        // Found at least one item in this section
        hasItemsInSection = true;
        break;
      }
      return hasItemsInSection;
    }
    return true; // Keep all non-header items
  });

  const isFtueConsumerContext =
    selectedContext === "RESIDENTIAL" || selectedContext === "COMMERCIAL";
  const isAwaitingFtueDecision =
    welcomeOnboardingEnabled &&
    Boolean(user?.id) &&
    isFtueConsumerContext &&
    !hasLocallyCompletedOnboarding &&
    !showOnboarding &&
    (isFtueSettingsLoading || !ftueSettings);

  if (location.pathname === "/onboarding" && isAwaitingFtueDecision) {
    return (
      <div
        className={styles.onboardingLoading}
        role="status"
        aria-label="Loading onboarding status"
      >
        <Spinner size="large" />
      </div>
    );
  }

  if (showOnboarding && location.pathname === "/onboarding") {
    return (
      <OnboardingFlow
        userRole={activeRole}
        onComplete={dismissOnboarding}
      />
    );
  }

  return (
    <div className={styles.layout}>
      {/* Header - Fixed at top spanning full width */}
      <GlobalHeader
        contextOptions={contextOptions}
        selectedContext={selectedContext || ""}
        onContextChange={handleContextChange}
        companyOptions={
          selectedContext === "COMMERCIAL" && consumerUsers.length > 1
            ? consumerUsers.map((_, i) => ({
              value: String(i),
              label: t("layout.company", { index: i + 1 }),
            }))
            : undefined
        }
        selectedCompany={String(selectedCompanyIndex)}
        onCompanyChange={(value) => {
          const index = parseInt(value, 10);
          if (!isNaN(index) && index !== selectedCompanyIndex) {
            selectCompany(index);
          }
        }}
        userName={customerName || user?.name || ""}
        showProfile={Boolean(user && activeRole)}
        onMyProfile={() => navigate("/profile")}
        onManageResideoId={handleManageResideoId}
        onSignOut={handleLogout}
      />

      {/* Content below header */}
      <div className={styles.contentWrapper}>
        {/* Left Hamburger Sidebar (tablet/mobile only) */}
        <div className={styles.hamburgerSidebar}>
          <Button
            className={styles.hamburgerButton}
            onClick={toggleMobileMenu}
            aria-label={t("layout.toggleMenu")}
            style={{ background: 'transparent', border: 'none', padding: 0 }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 12h18M3 6h18M3 18h18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </Button>
        </div>

        {/* Left Location Sidebar - Hide for PARTNER context */}
        {selectedContext !== "PARTNER" && (
          <LocationSelector
            isMobileMenuOpen={isMobileMenuOpen}
            newLocationIds={newLocationIds}
            showLocationGuidanceSteps={!Boolean(ftueSettings?.seenFirstNewLocation)}
            onLocationGuidanceComplete={markLocationGuidanceComplete}
          />
        )}

        {/* Main Navigation Sidebar */}
        <aside
          className={`${styles.navSidebar} ${isMobileMenuOpen ? styles.navSidebarMobileOpen : ""}`}
        >
          <nav className={`${styles.nav} ${selectedLocation ? styles.locationLevel : styles.orgLevel}`}>
            {visibleNavItems.map((item, index) => {
              if ("type" in item && item.type === "header") {
                return (
                  <div key={`header-${index}`} className={styles.navHeader}>
                    {item.icon && (
                      <span className={styles.navItemIcon}>{item.icon}</span>
                    )}
                    <span>{item.label}</span>
                  </div>
                );
              }

              // Check for exact match or if this is a parent route
              const itemPath = "path" in item ? item.path : undefined;
              const itemPathname = itemPath?.split("?")[0];
              const itemBadge = "badge" in item ? item.badge : undefined;
              const isActive = itemPathname
                ? location.pathname === itemPathname ||
                location.pathname.startsWith(`${itemPathname}/`)
                : false;

              const isExpanded = selectedLocation
                ? true
                : expandedItems.includes(item.label);

              // Item with submenu (for OPERATIONS items like Cameras, HVAC, Access)
              const itemSubmenu = "submenu" in item ? item.submenu : undefined;
              if (itemSubmenu && itemSubmenu.length > 0) {
                // Filter submenu items by permission and menu level
                const visibleSubmenu = itemSubmenu.filter((subItem) => {
                  if (!shouldShowMenuItem(subItem.permission)) return false;
                  if (!subItem.menuLevel) return true;
                  return subItem.menuLevel === activeMenuLevel || subItem.menuLevel === "both";
                });

                return (
                  <div
                    key={itemPath || `item-${index}`}
                    className={styles.navGroup}
                  >
                    <Button
                      type="button"
                      variant="text"
                      className={`${styles.navItem} ${isActive && !isExpanded ? styles.navItemActive : ""
                        } ${isExpanded ? styles.navItemExpanded : ""}`}
                      onClick={() => {
                        if (!isExpanded) {
                          const onExpand = (item as MenuItem).onExpand;
                          if (onExpand) {
                            Promise.resolve(onExpand()).catch((error) => {
                              console.error(
                                "Error in menu onExpand handler",
                                error,
                              );
                            });
                          }
                        }
                        toggleSubmenu(item.label);
                      }}
                    >
                      {item.icon && (
                        <span className={styles.navItemIcon}>
                          {typeof item.icon === "function" ? item.icon() : item.icon}
                        </span>
                      )}
                      <span className={styles.navItemLabel}>{item.label}</span>
                      <svg
                        className={`${styles.navItemChevron} ${isExpanded ? styles.navItemChevronExpanded : ""
                          }`}
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {itemBadge && (
                        <span className={styles.navItemBadge}>{itemBadge}</span>
                      )}
                    </Button>

                    {isExpanded && (
                      <div className={styles.submenuContainer}>
                        <div className={styles.submenuList}>
                          {visibleSubmenu.map((subItem, subIndex) => {
                            const isSubActive =
                              location.pathname === subItem.path;
                            return (
                              <div
                                key={subItem.path || `sub-${subIndex}`}
                                className={styles.submenuItemWrapper}
                              >
                                <NavLink
                                  to={subItem.path}
                                  className={`${styles.submenuItem} ${isSubActive ? styles.submenuItemActive : ""
                                    }`}
                                  onClick={() => {
                                    // Close mobile menu when submenu item is clicked
                                    if (isMobileMenuOpen) {
                                      setIsMobileMenuOpen(false);
                                    }
                                  }}
                                  onMouseEnter={() => {
                                    void subItem.onHover?.();
                                  }}
                                >
                                  <span className={styles.menuLabel}>
                                    {subItem.label}
                                  </span>
                                </NavLink>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // Item without submenu (flat menu items)
              const itemIcon = "icon" in item ? item.icon : undefined;
              return (
                <Link
                  key={itemPath || `item-${index}`}
                  to={itemPath || "/"}
                  className={`${styles.navItem} ${isActive ? styles.navItemActive : ""
                    }`}
                  onClick={() => {
                    // Close mobile menu when menu item is clicked
                    if (isMobileMenuOpen) {
                      setIsMobileMenuOpen(false);
                    }
                  }}
                >
                  {itemIcon && (
                    <span className={styles.navItemIcon}>
                      {typeof itemIcon === "function" ? itemIcon() : itemIcon}
                    </span>
                  )}
                  <span className={styles.navItemLabel}>{item.label}</span>
                  {itemBadge && (
                    <span className={styles.navItemBadge}>{itemBadge}</span>
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className={styles.main}>
          {isRefreshingPermissions && (
            <div className={styles.permissionOverlay}>
              <Spinner size="large" />
            </div>
          )}
          <div className={styles.content}>
            {isAwaitingFtueDecision ? (
              <div
                className={styles.permissionOverlay}
                role="status"
                aria-label="Loading onboarding status"
              >
                <Spinner size="large" />
              </div>
            ) : (
              <Outlet />
            )}
          </div>
        </main>
        {isMobileMenuOpen && (
          <div
            className={styles.mobileOverlay}
            onClick={toggleMobileMenu}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleMobileMenu();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={t("layout.closeMenu")}
          />
        )}
      </div>
    </div>
  );
};
